'use client';

import { EmblaCarouselType } from 'embla-carousel';
import Image from 'next/image';
import Link from 'next/link';

import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

import {
  contentTabs,
  membersCard,
  membersCardImg,
  membersCardInfo,
  membersCardLink,
  membersCardName,
  membersCardTitle,
  membersPanel,
  membersPanels,
  membersSlides,
} from '@/app/[locale]/(new)/(public)/projects/[slug]/_page.css';
import { Carousel } from '@/app/_componentsv2/carousels/carousel';
import { CarouselNav } from '@/app/_componentsv2/carousels/carousel-nav';
import { IconLinkedin } from '@/app/_componentsv2/icons/linkedin';
import { TabPanels } from '@/app/_componentsv2/tab-panels';
import { Tabs } from '@/app/_componentsv2/tabs';
import { Member } from '@/domain/project/types';

interface MembersProps {
  member: Member[];
  board: Member[];
}

export function Members({ member, board }: MembersProps) {
  const t = useTranslations();

  const [selected, setSelected] = useState('member');

  const [emblaApi, setEmblaApi] = useState<Record<string, EmblaCarouselType>>(
    {},
  );
  const [selectedSlide, setSelectedSlide] = useState(0);

  const groups = {
    member: {
      label: t(`pages.project.members.member.title`),
      members: member,
    },
    board: {
      label: t(`pages.project.members.board.title`),
      members: board,
    },
  };

  const currentEmblaApi = emblaApi[selected];

  useEffect(() => {
    if (currentEmblaApi === undefined) {
      return;
    }

    const subscription = currentEmblaApi.on('select', () =>
      setSelectedSlide(currentEmblaApi.selectedScrollSnap()),
    );

    return () => subscription.clear();
  }, [currentEmblaApi, selected]);

  useEffect(() => {
    if (currentEmblaApi === undefined) {
      return;
    }

    currentEmblaApi.scrollTo(selectedSlide);
  }, [selected, selectedSlide, currentEmblaApi]);

  return (
    <section>
      <Tabs
        classNames={{ tabs: contentTabs }}
        data={Object.entries(groups)}
        label={([, { label }]) => label}
        onSelect={([key]) => setSelected(key)}
        selected={selected}
        value={([key]) => key}
      />
      <section>
        <TabPanels
          classNames={{
            panels: membersPanels,
            panel: membersPanel,
          }}
          data={Object.entries(groups)}
          selected={selected}
          value={([key]) => key}
        >
          {([key, { members }]) => {
            return (
              <>
                <Carousel
                  emblaApiRef={(api) =>
                    setEmblaApi((current) =>
                      current[key] !== api
                        ? { ...current, [key]: api }
                        : current,
                    )
                  }
                  classNames={{ slides: membersSlides }}
                >
                  {members.map((member) => (
                    <section className={membersCard} key={member.name}>
                      <Image
                        className={membersCardImg}
                        width={200}
                        height={300}
                        alt={member.name}
                        src={member.photo}
                      />
                      <section className={membersCardTitle}>
                        <span className={membersCardName}>{member.name}</span>
                        {member.url ? (
                          <Link
                            className={membersCardLink}
                            href={member.url}
                            target="_blank"
                            rel="nofollow"
                          >
                            <IconLinkedin />
                          </Link>
                        ) : null}
                      </section>

                      <span className={membersCardInfo}>{member.job}</span>
                    </section>
                  ))}
                </Carousel>
                <section>
                  <CarouselNav
                    bullets={members.length}
                    selected={selectedSlide}
                    onSelect={setSelectedSlide}
                  />
                </section>
              </>
            );
          }}
        </TabPanels>
      </section>
    </section>
  );
}
